"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  computeIv,
  type IvMode,
} from "@/components/tools/stats-clinical/iv-compute";
import {
  CUSTOM_FACTOR,
  FactorFields,
} from "@/components/tools/stats-clinical/iv-fields";
import { IvPractice } from "@/components/tools/stats-clinical/iv-practice";
import { IvResults } from "@/components/tools/stats-clinical/iv-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { parseNumberInput } from "@/lib/parse-number";

/** English strings. A translated content file overrides these through `ui`. */
export const IV_DRIP_RATE_CALCULATOR_STRINGS = {
  disclaimerHeading: "Practice calculator — not for patient care",
  disclaimer:
    "This page is for learning dosage-calculation math. It knows nothing about any real order, patient or pump. Real infusions follow the prescriber's order, your facility's policy, the pump's own programming and an independent double-check.",
  modeLabel: "What do you want to find?",
  modeVolumeTime: "mL/h and drops per minute, from volume and time",
  modeFlow: "Drops per minute, from a pump rate in mL/h",
  modeDuration: "How long an infusion will take",
  volumeLabel: "Total volume (mL)",
  volumePlaceholder: "1000",
  hoursLabel: "Hours",
  minutesLabel: "Minutes",
  flowLabel: "Rate (mL/h)",
  flowPlaceholder: "125",
  dropFactorLabel: "Drop factor (gtt/mL)",
  dropFactorHint:
    "Printed on the tubing package. Macrodrip is usually 10, 12, 15 or 20; microdrip is 60.",
  dropFactorOption: "{value} gtt/mL",
  dropFactorMicro: "{value} gtt/mL (microdrip)",
  dropFactorCustom: "Another value",
  customDropFactor: "Drop factor (gtt/mL)",
  rateGivenLabel: "The rate is given as",
  rateMlh: "mL per hour",
  rateGtt: "Drops per minute",
  gttLabel: "Rate (gtt/min)",
  gttPlaceholder: "31",
  roundingLabel: "Round drops per minute to",
  roundWhole: "A whole drop",
  roundTenth: "One decimal",
  invalid:
    "Enter positive numbers: a volume, a time above zero and, where it is needed, a drop factor.",
  enterPrompt: "Enter a volume, a time and a drop factor to see the working.",
  results: "Results",
  cardFlow: "Flow rate (mL/h)",
  cardDrip: "Drip rate (gtt/min)",
  cardTime: "Infusion time",
  hoursCount: "one: {count} hour|other: {count} hours",
  minutesCount: "one: {count} minute|other: {count} minutes",
  workingHeading: "Working, with the units",
  stepFlow: "Flow rate = {volume} mL ÷ {hours} h = {rate} mL/h",
  stepDrip:
    "Drip rate = {volume} mL × {df} gtt/mL ÷ {minutes} min = {raw} gtt/min, which rounds to {rounded} gtt/min",
  stepDripFlow:
    "Drip rate = {rate} mL/h × {df} gtt/mL × (1 h ÷ 60 min) = {raw} gtt/min, which rounds to {rounded} gtt/min",
  stepUnits:
    "Unit check: mL × gtt/mL ÷ min — the mL cancel, leaving gtt/min. In the second form, mL/h × gtt/mL × h/min: the mL and the hours cancel.",
  stepMicro:
    "With 60 gtt/mL tubing the 60 drops per mL cancel the 60 minutes per hour, so gtt/min has the same number as mL/h.",
  stepTime: "Time = {volume} mL ÷ {rate} mL/h = {hours} h, which is {duration}",
  stepTimeDrip:
    "Time = {volume} mL × {df} gtt/mL ÷ {rate} gtt/min = {minutes} min, which is {duration}",
  stepTimeUnits:
    "Unit check: mL ÷ (mL/h) = h; mL × (gtt/mL) ÷ (gtt/min) = min.",
  roundingNote:
    "Rounding follows a common classroom rule (nearest whole drop). Your course or facility may round differently — check yours.",
  practiceHeading: "Practice problem {number} of {total}",
  problemGtt:
    "A practice order says to infuse {volume} mL over {time}. The tubing has a drop factor of {df} gtt/mL. How many drops per minute?",
  problemMlh:
    "A practice order says to infuse {volume} mL over {time} by pump. What rate in mL/h would you set?",
  problemTime:
    "A practice infusion of {volume} mL runs at {rate} mL/h. How long will it take?",
  answerGtt:
    "{volume} mL × {df} gtt/mL ÷ {minutes} min = {raw} gtt/min, so {rounded} gtt/min.",
  answerMlh: "{volume} mL ÷ {hours} h = {raw} mL/h, so {rounded} mL/h.",
  answerTime: "{volume} mL ÷ {rate} mL/h = {hours} h, which is {duration}.",
  showAnswer: "Show the answer",
  hideAnswer: "Hide the answer",
  nextProblem: "Next problem",
  practiceNote:
    "All practice orders are invented for the exercise and name no drug.",
} as const;

function dropFactorValue(selected: string, custom: string): number | null {
  if (selected !== CUSTOM_FACTOR) {
    return Number(selected);
  }
  const value = parseNumberInput(custom);
  return value !== null && value > 0 ? value : null;
}

export default function IvDripRateCalculatorWidget() {
  const strings = useToolUi(IV_DRIP_RATE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<IvMode>("volumeTime");
  const [volumeText, setVolumeText] = useState("");
  const [hoursText, setHoursText] = useState("");
  const [minutesText, setMinutesText] = useState("");
  const [flowText, setFlowText] = useState("");
  const [gttText, setGttText] = useState("");
  const [rateGiven, setRateGiven] = useState<"mlh" | "gtt">("mlh");
  const [dropFactor, setDropFactor] = useState("15");
  const [customFactor, setCustomFactor] = useState("");
  const [rounding, setRounding] = useState("0");

  const volume = parseNumberInput(volumeText);
  const hours = hoursText.trim() === "" ? 0 : parseNumberInput(hoursText);
  const minutes = minutesText.trim() === "" ? 0 : parseNumberInput(minutesText);
  const flow = parseNumberInput(flowText);
  const gtt = parseNumberInput(gttText);
  const factor = dropFactorValue(dropFactor, customFactor);
  const decimals = Number(rounding);
  const entered = [volumeText, hoursText, minutesText, flowText, gttText].some(
    (v) => v.trim() !== ""
  );
  const output = computeIv(
    mode,
    { volume, hours, minutes, flow, gtt, factor, rateGiven, decimals },
    strings,
    fmt,
    locale
  );
  const needsDropFactor = mode !== "duration" || rateGiven === "gtt";
  const showFlowInput =
    mode === "flow" || (mode === "duration" && rateGiven === "mlh");

  return (
    <div className="space-y-6">
      <Notice>
        <strong>{strings.disclaimerHeading}.</strong> {strings.disclaimer}
      </Notice>
      <PillGroup
        label={strings.modeLabel}
        name="ivd-mode"
        onChange={setMode}
        options={[
          { value: "volumeTime", label: strings.modeVolumeTime },
          { value: "flow", label: strings.modeFlow },
          { value: "duration", label: strings.modeDuration },
        ]}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        {mode !== "flow" ? (
          <TextField
            id="ivd-volume"
            label={strings.volumeLabel}
            numeric
            onChange={setVolumeText}
            placeholder={strings.volumePlaceholder}
            value={volumeText}
          />
        ) : null}
        {mode === "volumeTime" ? (
          <>
            <TextField
              id="ivd-hours"
              label={strings.hoursLabel}
              numeric
              onChange={setHoursText}
              placeholder="8"
              value={hoursText}
            />
            <TextField
              id="ivd-minutes"
              label={strings.minutesLabel}
              numeric
              onChange={setMinutesText}
              placeholder="0"
              value={minutesText}
            />
          </>
        ) : null}
        {mode === "duration" ? (
          <PillGroup
            label={strings.rateGivenLabel}
            name="ivd-rate-given"
            onChange={setRateGiven}
            options={[
              { value: "mlh", label: strings.rateMlh },
              { value: "gtt", label: strings.rateGtt },
            ]}
            value={rateGiven}
          />
        ) : null}
        {showFlowInput ? (
          <TextField
            id="ivd-flow"
            label={strings.flowLabel}
            numeric
            onChange={setFlowText}
            placeholder={strings.flowPlaceholder}
            value={flowText}
          />
        ) : null}
        {mode === "duration" && rateGiven === "gtt" ? (
          <TextField
            id="ivd-gtt"
            label={strings.gttLabel}
            numeric
            onChange={setGttText}
            placeholder={strings.gttPlaceholder}
            value={gttText}
          />
        ) : null}
      </div>
      <FactorFields
        customFactor={customFactor}
        dropFactor={dropFactor}
        onCustom={setCustomFactor}
        onDropFactor={setDropFactor}
        onRounding={setRounding}
        rounding={rounding}
        showFactor={needsDropFactor}
        showRounding={mode !== "duration"}
        strings={strings}
      />
      {entered && !output ? <ErrorNotice>{strings.invalid}</ErrorNotice> : null}
      {output ? (
        <IvResults mode={mode} output={output} strings={strings} />
      ) : null}
      {entered ? null : <Notice>{strings.enterPrompt}</Notice>}
      <IvPractice fmt={fmt} locale={locale} strings={strings} />
    </div>
  );
}
